import React, { useCallback, useState } from 'react';
import { TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

import { BadgeName } from '@/features/player-session/types/types';
import { StepViewMode } from './player-session-card.utils';
import { StepDetailNotesSheet } from './step-detail-notes-sheet';

export function StepDetailPanel({
  levelColor,
  levelNumber,
  skillName,
  title,
  body,
  mode,
  isLoading,
  activeBadge,
  onComplete,
  onIncomplete,
  onTryAgain,
  onSkip,
}: {
  levelColor: string;
  levelNumber: number;
  skillName: string;
  activeStepIdx: number;
  stepCount: number;
  title: string;
  body: string;
  mode: StepViewMode;
  isLoading?: boolean;
  onComplete: () => void;
  onIncomplete: () => void;
  onTryAgain: () => void;
  onSkip: () => void;
  activeBadge: {
    name: BadgeName;
    label: string;
  } | null;
}) {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const disableActions = Boolean(isLoading);
  const [notesOpen, setNotesOpen] = useState(false);

  const onPressChecklist = useCallback(() => {
    setNotesOpen(true);
  }, []);

  return (
    <Stack paddingX={scale(0)} paddingY={scale(8)}>
      <Stack direction="row" align="center" justify="space-between">
        <Stack direction="row" align="center" gap={scale(6)} flex={1} paddingX={scale(10)}>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={onPressChecklist}
            hitSlop={scale(10)}
            style={styles.checklistIconWrap}>
            <SvgIcon
              name="IcCheckList"
              size={scale({ phone: 26, tablet: 32 })}
              color={levelColor}
            />
          </TouchableOpacity>
          <Stack flex={1} gap={scale(1)}>
            <Text
              variant={isTablet ? 'Font Inter/Subtitle/Bold 14px' : 'Font Inter/Subtitle/Bold 11px'}
              style={[styles.stepTitle, { color: levelColor }]}
              numberOfLines={1}>
              {title}
            </Text>
            <Text
              variant={
                isTablet ? 'Font Inter/Subtitle/Regular 11px' : 'Font Inter/Subtitle/Regular'
              }
              style={styles.stepBody}
              numberOfLines={1}>
              {body}
            </Text>
            {mode === StepViewMode.Default ? (
              <Stack
                paddingTop={scale(8)}
                direction="row"
                gap={scale(10)}
                align="center"
                justify="center">
                <TouchableOpacity
                  activeOpacity={0.8}
                  disabled={disableActions}
                  style={[styles.primaryBtn, disableActions && styles.btnDisabled]}
                  onPress={onComplete}>
                  <Text style={styles.primaryBtnText}>
                    {STRINGS.playerSession.stepDetail.complete}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  activeOpacity={0.8}
                  disabled={disableActions}
                  style={[styles.secondaryBtn, disableActions && styles.btnDisabled]}
                  onPress={onIncomplete}>
                  <Text style={styles.secondaryBtnText}>
                    {STRINGS.playerSession.stepDetail.incomplete}
                  </Text>
                </TouchableOpacity>
              </Stack>
            ) : (
              <Stack
                paddingTop={scale(8)}
                direction="row"
                gap={scale(10)}
                align="center"
                justify="center">
                <TouchableOpacity
                  activeOpacity={0.8}
                  disabled={disableActions}
                  style={[styles.primaryBtn, disableActions && styles.btnDisabled]}
                  onPress={onTryAgain}>
                  <Text style={styles.primaryBtnText}>
                    {STRINGS.playerSession.stepDetail.tryAgain}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  activeOpacity={0.8}
                  disabled={disableActions}
                  style={[styles.secondaryBtn, disableActions && styles.btnDisabled]}
                  onPress={onSkip}>
                  <Text style={styles.secondaryBtnText}>
                    {STRINGS.playerSession.stepDetail.skip}
                  </Text>
                </TouchableOpacity>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>

      <StepDetailNotesSheet
        open={notesOpen}
        onOpenChange={setNotesOpen}
        levelColor={levelColor}
        levelNumber={levelNumber}
        skillName={skillName}
        activeBadge={activeBadge}
      />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  arrowButton: {},
  arrowDisabled: {
    opacity: 0.3,
  },
  btnDisabled: {
    opacity: 0.7,
  },
  checklistIconWrap: {},
  stepTitle: {
    textTransform: 'none',
  },
  stepBody: {
    color: colors['6'],
    fontSize: scale(10),
  },
  primaryBtn: {
    backgroundColor: colors.Blue,
    borderRadius: scale(4),
    paddingHorizontal: scale(14),
    height: scale({ phone: 26, tablet: 32 }),
    minWidth: scale({ phone: 110, tablet: 140 }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnText: {
    color: colors['0'],
    fontSize: scale({ phone: 10, tablet: 11 }),
    fontWeight: '400',
    fontFamily: 'Inter',
  },
  secondaryBtn: {
    backgroundColor: colors['0'],
    borderRadius: scale(4),
    borderWidth: 1,
    borderColor: colors.Blue,
    paddingHorizontal: scale(14),
    height: scale({ phone: 26, tablet: 32 }),
    minWidth: scale({ phone: 110, tablet: 140 }),
    justifyContent: 'center',
    alignItems: 'center',
  },
  secondaryBtnText: {
    color: colors.Blue,
    fontSize: scale({ phone: 10, tablet: 11 }),
    fontWeight: '400',
    fontFamily: 'Inter',
  },
}));
